import React from 'react'
import { Controller, Control } from 'react-hook-form'
import {
  FormHelperText,
  Select as MuiSelect,
  MenuItem,
  InputLabel,
  FormControl,
  ListItemText,
  Checkbox,
  Typography,
  SelectChangeEvent,
  FormControlProps
} from '@mui/material'
import { useTranslation } from '@contexts/translation'
import { SelectSkeleton } from '@components/common/Skeletons'

interface ElementItem {
  [key: string]: any
  is_group?: boolean
  group?: string | number
}

interface Elements {
  /** Array of items to display in the select */
  items: ElementItem[]
  /** Field name to use as the item ID (e.g., 'id', 'pk') */
  id: string
  /** Field name to use as the display value (e.g., 'name', 'display', 'title') */
  display: string
}

interface SelectItemProps extends Omit<FormControlProps, 'defaultValue'> {
  /** Field name in the form */
  name: string
  /** Label text for the field */
  label: string
  /** React Hook Form control object */
  control: Control<any>
  /** Default value for the field */
  defaultValue?: string | string[]
  /** Whether the component is in a loading state */
  isLoading?: boolean
  /** Whether multiple selections are allowed */
  multiple?: boolean
  /** Error message to display */
  errorMessage?: string
  /** Child components to render inside the select */
  children?: React.ReactNode
  /** Elements configuration for the select */
  elements?: Elements
  /** Whether to show a count of selected items */
  showCount?: boolean
  /** Function to set the selected operation ID */
  setSelectedOpId?: (id: string) => void
  /** Help text to display below the select */
  helpText?: string
  /** Test ID for component testing */
  data_testid?: string
}

/**
 * SelectItem component
 * A select component that integrates with React Hook Form
 */
const SelectItem = ({
  name,
  label,
  control,
  defaultValue,
  isLoading = false,
  multiple = false,
  errorMessage,
  children,
  elements = { items: [], id: '', display: '' },
  showCount = false,
  setSelectedOpId,
  helpText = '',
  data_testid,
  ...props
}: SelectItemProps): JSX.Element => {
  const { t } = useTranslation()
  const labelId = `${name}-label`

  return (
    <>
      {!isLoading && (
        <FormControl {...props} variant="filled" margin="normal">
          <InputLabel id={labelId}>{label}</InputLabel>
          <Controller
            name={name}
            control={control}
            defaultValue={defaultValue}
            render={({ field }) => {
              return (
                <MuiSelect
                  labelId={labelId}
                  label={label}
                  data-testid={data_testid}
                  multiple={multiple}
                  value={
                    isLoading && multiple ? [] : isLoading ? '' : field.value
                  }
                  renderValue={
                    multiple && showCount
                      ? (selected: any) => {
                          // Handle translation with a fallback
                          const count = (selected as any[]).length
                          return t
                            ? t('select_number_selected') || `${count} selected`
                            : `${count} selected`
                        }
                      : multiple && !children
                      ? (selected: any) => {
                          const retElems = elements.items
                            .filter(el =>
                              (selected as any[]).includes(el[elements.id])
                            )
                            .map(el => el[elements.display])
                          if (retElems.every(el => typeof el === 'string'))
                            return retElems.join(', ')
                          return retElems
                        }
                      : undefined
                  }
                  {...field}
                  onChange={(e: SelectChangeEvent<unknown>) => {
                    const value = e.target.value
                    if (e.target?.name === 'project' && setSelectedOpId) {
                      setSelectedOpId(value as string)
                    }
                    if (multiple && children && Array.isArray(value)) {
                      if (value[value.length - 1] === 'all') {
                        field.onChange(
                          (field.value as string[]).length ===
                            elements.items.length
                            ? []
                            : elements.items.map(el => el[elements.id])
                        )

                        return
                      } else if (
                        value[value.length - 1]?.toString().includes('group')
                      ) {
                        const groupValue = (
                          value[value.length - 1] as string
                        ).split('_')[1]
                        const itemsGroup = elements.items
                          .filter(el => el.group == groupValue && !el.is_group)
                          .map(el => el[elements.id])
                        field.onChange(
                          (field.value as string[]).filter(v =>
                            itemsGroup.includes(v)
                          ).length === itemsGroup.length
                            ? (field.value as string[]).filter(
                                v => !itemsGroup.includes(v)
                              )
                            : [...(field.value as string[]), ...itemsGroup]
                        )

                        return
                      }
                    }
                    field.onChange(value)
                  }}
                  sx={{
                    height: '45px'
                  }}
                >
                  {children &&
                    (isLoading ? (
                      <MenuItem
                        disabled
                        value=""
                        data-testid="MenuItem-88fc8a78-d9e9-4ce9-b63b-e74c145fb5a8"
                      >
                        {t('select_loading') || 'Loading...'}
                      </MenuItem>
                    ) : !React.Children.count(children) ? (
                      <MenuItem
                        disabled
                        value=""
                        data-testid="MenuItem-f8bdbc90-7889-4564-a373-ba2659f6d8c4"
                      >
                        {t('select_no_data') || 'No data'}
                      </MenuItem>
                    ) : (
                      children
                    ))}

                  {multiple && !children && (
                    <MenuItem
                      value="all"
                      data-testid="MenuItem-4df45535-cfc8-416e-8e22-12d8cb35b33b"
                    >
                      <Checkbox
                        sx={{ mr: 1 }}
                        checked={
                          (field.value as string[]).length ===
                          elements.items.length
                        }
                        indeterminate={
                          (field.value as string[]).length <
                            elements.items.length &&
                          (field.value as string[]).length > 0
                        }
                        data-testid="Checkbox-1f2bd98a-424b-437d-977c-7ffdc9d3547b"
                      />

                      <b>{t('select_select_all') || 'Select All'}</b>
                    </MenuItem>
                  )}

                  {multiple &&
                    !children &&
                    elements.items.map(el => {
                      const itemsGroup = elements.items
                        .filter(it => it.group == el.group && !it.is_group)
                        .map(it => it[elements.id])
                      const valueFiltered = (field.value as string[]).filter(
                        v => itemsGroup.includes(v)
                      )

                      return el.is_group ? (
                        <MenuItem
                          key={el.group?.toString()}
                          sx={{ display: 'flex', alignItems: 'center' }}
                          value={`group_${el.group}`}
                          data-testid="MenuItem-a206b275-201a-498a-9350-1689a01c073f"
                        >
                          <Checkbox
                            sx={{ mr: 1 }}
                            checked={itemsGroup.length === valueFiltered.length}
                            indeterminate={
                              itemsGroup.length !== valueFiltered.length &&
                              valueFiltered.length > 0
                            }
                            data-testid="Checkbox-a54713fd-9a2e-4dd1-90d1-a7958ae0b025"
                          />

                          <b>{el[elements.display]}</b>
                        </MenuItem>
                      ) : (
                        <MenuItem
                          key={el[elements.id]?.toString()}
                          value={el[elements.id]}
                          data-testid="MenuItem-f3e95e15-d2ee-473e-91a6-5f927c0946ff"
                        >
                          <Checkbox
                            sx={{ mr: 1, ml: el.is_group === false ? 2 : 0 }}
                            checked={
                              (field.value as any[]).indexOf(el[elements.id]) >
                              -1
                            }
                            data-testid="Checkbox-cf21813b-72f5-407f-acdb-43f83f46b176"
                          />

                          <ListItemText primary={el[elements.display]} />
                        </MenuItem>
                      )
                    })}
                </MuiSelect>
              )
            }}
          />

          {errorMessage && (
            <FormHelperText error>{errorMessage}</FormHelperText>
          )}

          {helpText && (
            <Typography
              sx={{ marginTop: 1 }}
              data-testid="Typography-1286b129-059b-43e3-bc47-4b10dcb9aff9"
            >
              {helpText}
            </Typography>
          )}
        </FormControl>
      )}

      {isLoading && <SelectSkeleton />}
    </>
  )
}

export default SelectItem
